<template>
  <Modal position="end" ref="modalRef" @before-show="populateForm" @after-close="clearForm">
    <template #header>{{ isAdding ? agentCopy.addModalTitle : agentCopy.editModalTitle }}</template>

    <template #body>
      <FormGroup>
        <FormField :label="agentCopy.fieldLabel" :error="form.fieldErrors('showingAgentId')">
          <ShowingAgentPicker v-model="form.fields.showingAgentId" :property-id="propertyId" />

          <!-- A sibling Text rather than FormField's `hint`, which is hidden while the field has an error. -->
          <Text v-if="agentCopy.fieldHint" variant="body-small" color="secondary">
            {{ agentCopy.fieldHint }}
          </Text>
        </FormField>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isLoading" @click="save">Save</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { computed, ref } from 'vue';

import { Button, FormField, FormGroup, Modal, Text, useAlertsStore, useForm } from '@tallo/design-system';
import { getShowingAgentCopy, TourType, useAssignShowingAgent } from '@tallo/investor/showing';
import { ShowingAgentPicker } from '@tallo/investor/showing-agents';

const props = defineProps<{
  showingId: string;
  showingAgentId?: string;
  tourType?: TourType;
  propertyId?: string;
}>();

const agentCopy = computed(() => getShowingAgentCopy(props.tourType));

const alertStore = useAlertsStore();
const { mutateAsync, isLoading } = useAssignShowingAgent();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
// Taken when the modal opens: saving refetches the showing, which would retitle it to "Edit" while it closes
const isAdding = ref(false);
const form = useForm<{ showingAgentId: string | null }>(
  {
    showingAgentId: null,
  },
  {
    showingAgentId: computed(() => ({
      required: helpers.withMessage(agentCopy.value.fieldRequiredError, required),
    })),
  },
);

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function populateForm() {
  isAdding.value = !props.showingAgentId;
  form.fields.showingAgentId = props.showingAgentId ?? null;
}

function clearForm() {
  form.resetValidation();
}

async function save() {
  form.submit(async () => {
    try {
      await mutateAsync({ showingId: props.showingId, showingAgentId: form.fields.showingAgentId as string });

      close();
    } catch (error) {
      alertStore.catchError(error);
    }
  });
}

defineExpose({ open });
</script>
